import { PairedCostAdjustmentType } from '@aloft/toolkit-shared';
import { ProductColor } from '@aloft/toolkit-sui';

import { DetailFeatures } from '../../../../redux/constants.ts';
import { selectIncludeCostApproach } from '../../../../redux/features/selectors/orderSelectors.ts';
import {
  selectFeatureDetailsByIDAndFeature,
} from '../../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { RootState } from '../../../../redux/types.ts';
import { DetailLineWithValue } from '../../../../components/Analysis/ColumnComponents/DetailLineWithValue.tsx';
import { PLACEHOLDER } from '../../consts.ts';
import { ViewMode } from '../../types.ts';
import { FEATURE_ADJUSTMENT_SELECTORS } from './featureAdjustmentSelectors.ts';
import { FEATURE_COST_SELECTORS } from './featureCostSelectors.ts';

const EMPTY_ADJUSTMENT: { value: string; color: ProductColor } = {
  value: '',
  color: 'textTertiary',
};

type AdjustmentDetailProps = {
  id: string;
  feature: DetailFeatures;
  viewMode: Extract<ViewMode, 'adjustments'>;
  group: PairedCostAdjustmentType;
  firstInGroup: boolean;
  lastInGroup: boolean;
};

type FeatureDetailProps =
  | {
    id: string;
    feature: DetailFeatures;
    viewMode: Exclude<ViewMode, 'adjustments'>;
  }
  | AdjustmentDetailProps;

const AdjustmentFeatureDetail = ({
  id,
  feature,
  group,
  firstInGroup,
  lastInGroup,
}: Omit<AdjustmentDetailProps, 'viewMode'>) => {
  const details = useAppSelector((state: RootState) =>
    selectFeatureDetailsByIDAndFeature(state, id, feature),
  );

  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const adjSelector =
    (lastInGroup ? FEATURE_ADJUSTMENT_SELECTORS[group] : undefined) ?? (() => EMPTY_ADJUSTMENT);
  const adj = useAppSelector((state: RootState) => adjSelector(state, id));
  const hideManualCostValue = feature === 'manualCost' && !includeCostApproach;

  return (
    <DetailLineWithValue
      text={details}
      value={hideManualCostValue ? '' : adj?.value}
      valueColor={hideManualCostValue ? undefined : adj?.color}
      topBorder={firstInGroup}
    />
  );
};

const CostFeatureDetail = ({ id, feature }: Omit<FeatureDetailProps, 'viewMode' | 'group'>) => {
  const details = useAppSelector((state: RootState) =>
    selectFeatureDetailsByIDAndFeature(state, id, feature),
  );

  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const costSelector = FEATURE_COST_SELECTORS[feature] ?? (() => PLACEHOLDER);
  let costs = useAppSelector((state: RootState) => costSelector(state, id));
  if (feature === 'manualCost' && !includeCostApproach) {
    costs = '';
  }

  return <DetailLineWithValue text={details} value={costs} />;
};

export const FeatureDetail = ({ id, feature, viewMode }: FeatureDetailProps) => {
  const componentProps = { id, feature, viewMode };

  if (viewMode === 'adjustments') {
    return <AdjustmentFeatureDetail {...(componentProps as AdjustmentDetailProps)} />;
  }
  return <CostFeatureDetail {...componentProps} />;
};
